import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, RefreshControl, Alert } from 'react-native';
import { Text, Card, Button, TextInput, FAB, Modal, Portal, ActivityIndicator } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import api from '../../services/api';

const PlanesScreen = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    precio_mensual: '',
    max_usuarios: '',
    max_productos: '',
  });

  const loadPlanes = async () => {
    try {
      const data = await api.superAdmin.getPlanes();
      setPlanes(Array.isArray(data) ? data : data.planes || []);
    } catch (error) {
      console.error('Error cargando planes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlanes();
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadPlanes();
    setRefreshing(false);
  }, []);

  const openModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setForm({
        nombre: plan.nombre || '',
        descripcion: plan.descripcion || '',
        precio_mensual: String(plan.precio_mensual || 0),
        max_usuarios: String(plan.max_usuarios || 0),
        max_productos: String(plan.max_productos || 0),
      });
    } else {
      setEditingPlan(null);
      setForm({ nombre: '', descripcion: '', precio_mensual: '0', max_usuarios: '0', max_productos: '0' });
    }
    setModalVisible(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      Alert.alert('Error', 'El nombre del plan es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim(),
        precio_mensual: parseFloat(form.precio_mensual) || 0,
        max_usuarios: parseInt(form.max_usuarios) || 0,
        max_productos: parseInt(form.max_productos) || 0,
      };

      if (editingPlan) {
        await api.superAdmin.updatePlan(editingPlan.id, payload);
      } else {
        await api.superAdmin.createPlan(payload);
      }

      setModalVisible(false);
      loadPlanes();
      Alert.alert('Exito', editingPlan ? 'Plan actualizado' : 'Plan creado');
    } catch (error) {
      Alert.alert('Error', error.message || 'No se pudo guardar el plan');
    } finally {
      setSaving(false);
    }
  };

  const renderPlan = ({ item }) => (
    <Card style={styles.planCard}>
      <Card.Content>
        <View style={styles.planHeader}>
          <View style={{ flex: 1 }}>
            <Text style={styles.planName}>{item.nombre}</Text>
            <Text style={styles.planPrice}>
              {item.precio_mensual > 0 ? `L. ${parseFloat(item.precio_mensual).toFixed(2)}/mes` : 'Gratuito'}
            </Text>
          </View>
          <Button mode="text" onPress={() => openModal(item)} textColor="#007bff" compact>
            Editar
          </Button>
        </View>

        {item.descripcion && (
          <Text style={styles.planDesc}>{item.descripcion}</Text>
        )}

        <View style={styles.limitsRow}>
          <View style={styles.limitItem}>
            <Ionicons name="people-outline" size={16} color="#666" />
            <Text style={styles.limitText}>
              {item.max_usuarios > 0 ? `${item.max_usuarios} usuarios` : 'Ilimitados'}
            </Text>
          </View>
          <View style={styles.limitItem}>
            <Ionicons name="cube-outline" size={16} color="#666" />
            <Text style={styles.limitText}>
              {item.max_productos > 0 ? `${item.max_productos} productos` : 'Ilimitados'}
            </Text>
          </View>
        </View>

        {item.total_tenants !== undefined && (
          <Text style={styles.tenantCount}>{item.total_tenants} empresa(s) usando este plan</Text>
        )}
      </Card.Content>
    </Card>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={planes}
        renderItem={renderPlan}
        keyExtractor={(item) => String(item.id)}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="pricetag-outline" size={48} color="#ccc" />
            <Text style={styles.emptyText}>No hay planes configurados</Text>
          </View>
        }
      />

      <FAB
        icon="plus"
        style={[styles.fab, { bottom: 16 + insets.bottom }]}
        onPress={() => openModal()}
        label="Nuevo Plan"
      />

      {/* Modal de crear/editar plan */}
      <Portal>
        <Modal
          visible={modalVisible}
          onDismiss={() => setModalVisible(false)}
          contentContainerStyle={styles.modal}
        >
          <Text style={styles.modalTitle}>{editingPlan ? 'Editar Plan' : 'Nuevo Plan'}</Text>

          <TextInput
            label="Nombre del plan *"
            value={form.nombre}
            onChangeText={(v) => setForm(p => ({ ...p, nombre: v }))}
            style={styles.input}
          />

          <TextInput
            label="Descripcion"
            value={form.descripcion}
            onChangeText={(v) => setForm(p => ({ ...p, descripcion: v }))}
            style={styles.input}
            multiline
          />

          <TextInput
            label="Precio mensual (L.)"
            value={form.precio_mensual}
            onChangeText={(v) => setForm(p => ({ ...p, precio_mensual: v }))}
            style={styles.input}
            keyboardType="numeric"
          />

          <TextInput
            label="Max usuarios (0 = ilimitado)"
            value={form.max_usuarios}
            onChangeText={(v) => setForm(p => ({ ...p, max_usuarios: v }))}
            style={styles.input}
            keyboardType="numeric"
          />

          <TextInput
            label="Max productos (0 = ilimitado)"
            value={form.max_productos}
            onChangeText={(v) => setForm(p => ({ ...p, max_productos: v }))}
            style={styles.input}
            keyboardType="numeric"
          />

          <View style={styles.modalButtons}>
            <Button mode="text" onPress={() => setModalVisible(false)} style={{ flex: 1 }}>
              Cancelar
            </Button>
            <Button
              mode="contained"
              onPress={handleSave}
              loading={saving}
              disabled={saving}
              style={{ flex: 1, backgroundColor: '#007bff' }}
            >
              {editingPlan ? 'Guardar' : 'Crear'}
            </Button>
          </View>
        </Modal>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  listContent: { padding: 12, paddingBottom: 80 },
  planCard: { marginBottom: 12, backgroundColor: 'white' },
  planHeader: { flexDirection: 'row', alignItems: 'center' },
  planName: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  planPrice: { fontSize: 16, color: '#007bff', fontWeight: '600', marginTop: 2 },
  planDesc: { fontSize: 13, color: '#666', marginTop: 8 },
  limitsRow: { flexDirection: 'row', gap: 20, marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#f0f0f0' },
  limitItem: { flexDirection: 'row', alignItems: 'center' },
  limitText: { fontSize: 13, color: '#666', marginLeft: 6 },
  tenantCount: { fontSize: 12, color: '#888', marginTop: 8, fontStyle: 'italic' },
  fab: { position: 'absolute', right: 16, bottom: 16, backgroundColor: '#007bff' },
  modal: { backgroundColor: 'white', margin: 20, borderRadius: 12, padding: 20 },
  modalTitle: { fontSize: 18, fontWeight: 'bold', color: '#333', marginBottom: 16 },
  input: { marginBottom: 10, backgroundColor: '#f9f9f9' },
  modalButtons: { flexDirection: 'row', gap: 10, marginTop: 16 },
  emptyContainer: { alignItems: 'center', paddingVertical: 40 },
  emptyText: { fontSize: 15, color: '#999', marginTop: 12 },
});

export default PlanesScreen;
